package io.horion.cv.api.utils

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.browser.uri.encodeURIComponent
import io.horion.cv.api.config.HttpClient.clientAuth
import io.horion.cv.api.datasource.Endpoint
import io.horion.cv.api.datasource.auth.Role
import io.horion.cv.api.utils.Constants.AUTH_FRONTEND_URL
import io.horion.cv.api.utils.Constants.AUTH_URL
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import kotlinx.browser.window
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.longOrNull
import kotlin.js.Date

object TokenRepository {

    var token by mutableStateOf<String?>(null)
        private set
    // On ne stock RIEN dans sessionStorage
    fun set(newToken: String?) {
        token = newToken
    }

    fun get(): String? = token

    fun clear() {
        token = null
    }

    fun getUserRoleFromToken(): Role? {
        val token = get() ?: return null
        return try {
            val parts = token.split(".")
            if (parts.size != 3) return null

            // Décodage de la partie "Payload" (index 1)
            val payload = window.atob(parts[1])
            val json = Json.parseToJsonElement(payload).jsonObject
            val roleString = json["role"]?.jsonPrimitive?.content

            Role.valueOf(roleString ?: "user")
        } catch (e: Exception) {
            null
        }
    }

    fun isUserLoggedIn(): Boolean {
        val currentToken = get() ?: return false // Récupère ton token via TokenRepository.get()

        return try {
            val parts = currentToken.split(".")
            if (parts.size != 3) return false

            // Décodage du payload
            val payload = window.atob(parts[1])
            val json = Json.parseToJsonElement(payload).jsonObject

            // "exp" est en secondes dans un JWT
            val exp = json["exp"]?.jsonPrimitive?.longOrNull ?: 0L

            // Date.now() renvoie des millisecondes, on divise par 1000
            val currentTimeInSeconds = (Date.now() / 1000).toLong()

            // Vérification simple : le temps actuel doit être inférieur à l'expiration
            currentTimeInSeconds < exp
        } catch (e: Exception) {
            false
        }
    }

    suspend fun checkAuthentication(): Boolean {
        // 1. Si on a déjà un token valide en mémoire
        if (isUserLoggedIn()) return true

        // 2. Sinon, on tente un échange (pour voir s'il y a un cookie de session valide)
        val newToken = exchangeToken()

        return newToken != null
    }

    fun getUuidFromToken(): String? {
        val token = get() ?: return null
        return try {
            val parts = token.split(".")
            if (parts.size != 3) return null

            // Décodage de la partie "Payload" (index 1)
            val payload = window.atob(parts[1])
            val json = Json.parseToJsonElement(payload).jsonObject
            val uuid = json["uuid"]?.jsonPrimitive?.content

            uuid
        } catch (e: Exception) {
            null
        }
    }

    suspend fun exchangeToken(): String? = withContext(Dispatchers.Main) {
        try {
            val response: HttpResponse = clientAuth.get("$AUTH_URL${Endpoint.Exchange.path}") {
                url { parameters.append("service", "CV") }
                accept(ContentType.Application.Json)
            }

            if (response.status == HttpStatusCode.OK) {
                val json = response.body<JsonObject>()
                val token = json["accessToken"]?.jsonPrimitive?.content
                token?.let { set(it) }
                token
            } else {
                null
            }
        } catch (e: Exception) {
            null
        }
    }

    fun redirectToAuth() {
        val currentUrl = window.location.href
        val encodedRedirect = encodeURIComponent(currentUrl)

        // Redirection vers le frontend de AUTH
        window.location.href = "$AUTH_FRONTEND_URL?redirect=$encodedRedirect"
        //window.location.href = "$AUTH_URL/login?service=CV"
    }

    suspend fun <T> apiWithTokenRetry(block: suspend (String) -> T?): T? {
        // 1️⃣ Récupère token actuel ou échange
        var token = get() ?: exchangeToken() ?: run {
            redirectToAuth() // pas de token, on redirige
            return null
        }

        // 2️⃣ Premier essai
        var result = block(token)
        if (result != null) return result

        token = exchangeToken() ?: run {
            redirectToAuth()
            return null
        }

        return block(token)
    }
}